import { useState, useMemo } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatPercent } from '../../lib/formatters'
import { calculateCarAffordability } from '../../lib/calculations/carAffordability'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', hint, min, step }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          min={min}
          step={step}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

function RuleBadge({ label, rule, passes, colors }) {
  return (
    <div className={`p-3 rounded-xl border ${passes ? 'border-positive/30' : 'border-negative/30'}`}
      style={{ background: passes ? `${colors.positive}10` : `${colors.negative}10` }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{passes ? '\u2705' : '\u274C'}</span>
        <span className="text-sm font-semibold text-heading">{label}</span>
      </div>
      <p className="text-xs text-muted">{rule}</p>
    </div>
  )
}

export default function CarAffordability() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [grossMonthlyIncome, setGrossMonthlyIncome] = useState(6000)
  const [vehiclePrice, setVehiclePrice] = useState(35000)
  const [downPayment, setDownPayment] = useState(7000)
  const [interestRate, setInterestRate] = useState(6.5)
  const [loanTerm, setLoanTerm] = useState(36)
  const [monthlyInvestment, setMonthlyInvestment] = useState(500)

  const result = useMemo(() => calculateCarAffordability({
    grossMonthlyIncome, vehiclePrice, downPayment, interestRate, loanTerm, monthlyInvestment,
  }), [grossMonthlyIncome, vehiclePrice, downPayment, interestRate, loanTerm, monthlyInvestment])

  const termOptions = [24, 36, 48, 60, 72]

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const margin = 14

    const tableStyles = {
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [41, 65, 122], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    }

    // --- Page 1: Title, Inputs, Key Results, Rules ---

    // Title
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Car Affordability Report', margin, 22)

    doc.setFontSize(11)
    doc.setTextColor(80, 80, 80)
    doc.text('The Money Guy 20/3/8 Rule', margin, 30)

    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text('Put 20%+ down, finance for 3 years or less, keep payments under 8% of gross income', margin, 36)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 42)

    // Input Summary
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Input Summary', margin, 54)

    const inputRows = [
      ['Gross Monthly Income', formatCurrency(grossMonthlyIncome)],
      ['Annual Income', formatCurrency(grossMonthlyIncome * 12)],
      ['Vehicle Price', formatCurrency(vehiclePrice)],
      ['Down Payment', `${formatCurrency(downPayment)} (${result.downPaymentPercent}%)`],
      ['Loan Amount', formatCurrency(vehiclePrice - downPayment)],
      ['Interest Rate', `${interestRate}%`],
      ['Loan Term', `${loanTerm} months`],
      ['Monthly Investing', formatCurrency(monthlyInvestment)],
    ]

    autoTable(doc, {
      body: inputRows,
      startY: 58,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // Key Results
    let y = doc.lastAutoTable.finalY + 12
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Key Results', margin, y)

    const resultRows = [
      ['Monthly Payment', formatCurrency(result.monthlyPayment)],
      ['Total Interest Paid', formatCurrency(result.totalInterest)],
      ['Total Cost of Loan', formatCurrency(result.monthlyPayment * loanTerm)],
      ['Total Cost of Ownership', formatCurrency(result.totalCost)],
      ['Payment as % of Income', `${result.paymentPercentOfIncome}%`],
      ['Max Affordable Price (8% rule)', formatCurrency(result.maxAffordablePrice)],
      ['Max Monthly Payment (8% rule)', formatCurrency(result.maxMonthlyPayment)],
      ['Overall Score', `${result.score}/4 rules passed`],
    ]

    autoTable(doc, {
      body: resultRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // 20/3/8 Rule Assessment
    y = doc.lastAutoTable.finalY + 12
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('20/3/8 Rule Assessment', margin, y)

    const ruleRows = [
      [
        '20% Down Payment',
        result.rule20 ? 'PASS' : 'FAIL',
        `${result.downPaymentPercent}% down (need 20%+)`,
      ],
      [
        '3-Year Term (36 months)',
        result.rule3 ? 'PASS' : 'FAIL',
        `${loanTerm} months (need 36 or less)`,
      ],
      [
        '8% of Gross Income',
        result.rule8 ? 'PASS' : 'FAIL',
        `${result.paymentPercentOfIncome}% of income (need 8% or less)`,
      ],
      [
        'Invest > Payment (Bonus)',
        result.bonusInvestment ? 'PASS' : 'FAIL',
        `Investing ${formatCurrency(monthlyInvestment)} vs ${formatCurrency(result.monthlyPayment)} payment`,
      ],
    ]

    autoTable(doc, {
      head: [['Rule', 'Status', 'Details']],
      body: ruleRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 }, 1: { cellWidth: 20, halign: 'center' } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          data.cell.styles.textColor = data.cell.raw === 'PASS' ? [22, 163, 74] : [220, 38, 38]
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // Overall verdict
    y = doc.lastAutoTable.finalY + 8
    doc.setFontSize(11)
    doc.setTextColor(30, 30, 30)
    doc.text(`Overall: ${result.score}/4 rules passed`, margin, y)
    doc.setFontSize(9)
    doc.setTextColor(80, 80, 80)
    doc.text(
      result.allRulesPass
        ? 'This vehicle purchase meets all 20/3/8 guidelines. Well done!'
        : 'This vehicle purchase does not meet all guidelines. Consider adjustments.',
      margin, y + 7
    )

    // --- Page 2: Loan Term Comparison ---
    doc.addPage()

    doc.setFontSize(16)
    doc.setTextColor(30, 30, 30)
    doc.text('Loan Term Comparison', margin, 22)
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text(`Loan amount: ${formatCurrency(vehiclePrice - downPayment)} at ${interestRate}% APR`, margin, 29)

    const termHead = [['Term', 'Monthly Payment', 'Total Interest', '% of Income', '3yr Rule', '8% Rule', 'Status']]
    const termBodyRows = result.termComparisons.map(row => [
      `${row.term} months`,
      formatCurrency(row.monthlyPayment),
      formatCurrency(row.totalInterest),
      `${row.percentOfIncome}%`,
      row.passes3Rule ? 'PASS' : 'FAIL',
      row.passes8Rule ? 'PASS' : 'FAIL',
      row.passes3Rule && row.passes8Rule ? 'Pass' : row.passes8Rule ? '8% OK' : 'Fail',
    ])

    autoTable(doc, {
      head: termHead,
      body: termBodyRows,
      startY: 34,
      ...tableStyles,
      theme: 'striped',
      didParseCell: (data) => {
        if (data.section === 'body') {
          // Color the status columns
          if (data.column.index === 4 || data.column.index === 5) {
            data.cell.styles.textColor = data.cell.raw === 'PASS' ? [22, 163, 74] : [220, 38, 38]
            data.cell.styles.fontStyle = 'bold'
            data.cell.styles.halign = 'center'
          }
          if (data.column.index === 6) {
            if (data.cell.raw === 'Pass') data.cell.styles.textColor = [22, 163, 74]
            else if (data.cell.raw === '8% OK') data.cell.styles.textColor = [202, 138, 4]
            else data.cell.styles.textColor = [220, 38, 38]
            data.cell.styles.fontStyle = 'bold'
            data.cell.styles.halign = 'center'
          }
          // Highlight the selected term row
          if (data.row.raw && data.row.raw[0] === `${loanTerm} months`) {
            data.cell.styles.fillColor = [230, 240, 255]
          }
        }
      },
    })

    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text(`* Highlighted row is your selected term (${loanTerm} months)`, margin, y)

    doc.save(`car-affordability-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h3 className="text-lg font-semibold text-heading mb-1">The Money Guy 20/3/8 Rule</h3>
            <p className="text-xs text-muted mb-4">Put 20%+ down, finance for 3 years or less, keep payments under 8% of gross income</p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Gross Monthly Income" value={grossMonthlyIncome} onChange={setGrossMonthlyIncome} prefix="$" />
          <InputField label="Vehicle Price" value={vehiclePrice} onChange={setVehiclePrice} prefix="$" />
          <InputField label="Down Payment" value={downPayment} onChange={setDownPayment} prefix="$" />
          <InputField label="Interest Rate" value={interestRate} onChange={setInterestRate} suffix="%" step={0.1} />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Loan Term</label>
            <div className="flex gap-1">
              {termOptions.map(t => (
                <button
                  key={t}
                  onClick={() => setLoanTerm(t)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    loanTerm === t ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={loanTerm === t ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {t}mo
                </button>
              ))}
            </div>
          </div>
          <InputField label="Monthly Investing" value={monthlyInvestment} onChange={setMonthlyInvestment} prefix="$" hint="Bonus rule: invest more than your payment" />
        </div>
      </GlassCard>

      {/* Rule Check Badges */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RuleBadge
          label="20% Down"
          rule={`${result.downPaymentPercent}% down (need 20%+)`}
          passes={result.rule20}
          colors={colors}
        />
        <RuleBadge
          label="3-Year Term"
          rule={`${loanTerm} months (need 36 or less)`}
          passes={result.rule3}
          colors={colors}
        />
        <RuleBadge
          label="8% of Income"
          rule={`${result.paymentPercentOfIncome}% of income (need 8% or less)`}
          passes={result.rule8}
          colors={colors}
        />
        <RuleBadge
          label="Invest > Payment"
          rule={`Investing ${formatCurrency(monthlyInvestment)} vs ${formatCurrency(result.monthlyPayment)} payment`}
          passes={result.bonusInvestment}
          colors={colors}
        />
      </div>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Monthly Payment" value={formatCurrency(result.monthlyPayment)} color={colors.primary} highlight />
        <ResultCard label="Total Interest" value={formatCurrency(result.totalInterest)} color={colors.negative} />
        <ResultCard label="Max Affordable Price" value={formatCurrency(result.maxAffordablePrice)} color={colors.positive} highlight />
        <ResultCard
          label="Score"
          value={`${result.score}/4 rules`}
          color={result.allRulesPass ? colors.positive : result.score >= 2 ? colors.warning : colors.negative}
        />
      </div>

      {/* Term Comparison Table */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Loan Term Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-card">
                <th className="text-left py-2 text-muted font-medium">Term</th>
                <th className="text-right py-2 text-muted font-medium">Payment</th>
                <th className="text-right py-2 text-muted font-medium">Total Interest</th>
                <th className="text-right py-2 text-muted font-medium">% of Income</th>
                <th className="text-center py-2 text-muted font-medium">Rules</th>
              </tr>
            </thead>
            <tbody>
              {result.termComparisons.map(row => (
                <tr key={row.term} className={`border-b border-card/50 ${row.term === loanTerm ? 'bg-primary/5' : ''}`}>
                  <td className="py-2 text-heading">{row.term} months</td>
                  <td className="py-2 text-right text-heading">{formatCurrency(row.monthlyPayment)}</td>
                  <td className="py-2 text-right" style={{ color: colors.negative }}>{formatCurrency(row.totalInterest)}</td>
                  <td className="py-2 text-right text-heading">{row.percentOfIncome}%</td>
                  <td className="py-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      row.passes3Rule && row.passes8Rule ? 'bg-positive/20 text-positive' :
                      row.passes8Rule ? 'bg-warning/20 text-warn' : 'bg-negative/20 text-negative'
                    }`}>
                      {row.passes3Rule && row.passes8Rule ? 'Pass' : row.passes8Rule ? '8% OK' : 'Fail'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  )
}
